import React, { useContext, useEffect, useState } from "react";
import { useParams, useHistory } from "react-router-dom";
import styles from "./CustomerProfile.module.scss";
import { LicenseHolderContext } from "../../shared/shared-with-mobile/providers/licenseHolder.provider";
import { LicenseTiersContext } from "../../shared/shared-with-mobile/providers/licenseTiers";
import { UIContext } from "../../shared/shared-with-mobile/providers/ui.provider";
import { APIService } from "../../shared/shared-with-mobile/api-client/api.service";
import { LicenseHolderModel } from "../../generated/from-api/models/license.model";
import { LicenseUsageModel } from "../../generated/from-api/models/license-usage.model";
import { UserProfileModel } from "../../generated/from-api/models/user-profile.model";
import { AdminUtility } from "./admin.utility";
import { getUserFacingSubscriptionName } from "../../generated/from-api/api-constants/license-tier-constants";
import { DateTime } from "luxon";
import Button from "../../components/Button/Button";
import TeamSetupIconCheck from "../../resources/icons/TeamSetupIconCheck";
import TeamSetupIconEmpty from "../../resources/icons/TeamSetupIconEmpty";
import { ResponsiveLine } from "@nivo/line";
import { AdminDataService } from "./mock-data/admin-data.service";
import { loadCustomerEngagement } from "./admin-real-data.service";
import SendLogTable from "./admin-components/SendLogTable";
import BillingTab from "./admin-components/BillingTab";
import HubspotTab from "./admin-components/HubspotTab";
import { EngagementRow } from "../../shared/shared-with-mobile/api-client/services/admin-analytics.service";
import { formatRelativeActivity } from "./admin-formatting";
import ManageLicenseModal2 from "./admin-components/ManageLicenseModal2";
import { LicenseOwnerListRow, SetupCriteria } from "./ManageLicensesAdmin";
import ChartRangePicker, {
  useChartRange,
  filterByRange,
} from "./admin-components/ChartRangePicker";

interface CustomerNote {
  id: string;
  text: string;
  createdAt: string;
  author: string;
}

const CustomerProfile: React.FC = () => {
  const { email } = useParams<{ email: string }>();
  const history = useHistory();
  const { dispatchToast } = useContext(UIContext);
  const { licenseHolders } = useContext(LicenseHolderContext);
  const { licenseTiers } = useContext(LicenseTiersContext);

  const [licenses, setLicenses] = useState<LicenseHolderModel[]>([]);
  const [usage, setUsage] = useState<LicenseUsageModel[]>([]);
  const [loading, setLoading] = useState(true);
  const [notes, setNotes] = useState<CustomerNote[]>([]);
  const [newNote, setNewNote] = useState("");
  const [tnReps, setTnReps] = useState<UserProfileModel[]>([]);
  const [engagement, setEngagement] = useState<EngagementRow | null>(null);
  type ActiveTab =
    | "overview"
    | "license"
    | "billing"
    | "hubspot"
    | "usage"
    | "intelligence"
    | "communications";
  // Initial tab can be driven by a URL hash so external pages (e.g. the
  // Customer Engagement table's link-status pills) can deep-link to a
  // specific section: /admin/customer/foo@bar.com#billing
  const initialTab: ActiveTab = (() => {
    const hash =
      typeof window !== "undefined"
        ? window.location.hash.replace("#", "")
        : "";
    const valid: ActiveTab[] = [
      "overview",
      "license",
      "billing",
      "hubspot",
      "usage",
      "intelligence",
      "communications",
    ];
    return (valid as string[]).includes(hash)
      ? (hash as ActiveTab)
      : "overview";
  })();
  const [activeTab, setActiveTab] = useState<ActiveTab>(initialTab);

  const decodedEmail = decodeURIComponent(email);

  useEffect(() => {
    async function loadCustomerData() {
      try {
        // licenseHolders is a Map<string, LicenseHolderModel[]> | undefined
        // Find the entry matching this email
        const licenseData: LicenseHolderModel[] = [];
        if (licenseHolders) {
          licenseHolders.forEach((holderLicenses) => {
            if (
              holderLicenses.length > 0 &&
              holderLicenses[0].email === decodedEmail
            ) {
              licenseData.push(...holderLicenses);
            }
          });
        }
        setLicenses(licenseData);

        // Get usage data
        const usageData = await APIService.LICENSE_QUOTA.LIST();
        const customerUsage = usageData.filter((u: LicenseUsageModel) =>
          licenseData.some((l) => l.id === u.licenseId)
        );
        setUsage(customerUsage);

        // TN reps and engagement load in parallel; both are best-effort.
        APIService.USER_REPRESENTATIVE.GET_LIST_OF_REPRESENTATIVES()
          .then(setTnReps)
          .catch(() => setTnReps([]));
        loadCustomerEngagement(decodedEmail)
          .then(setEngagement)
          .catch(() => setEngagement(null));
      } catch (err) {
        console.error("Error loading customer data:", err);
        dispatchToast({
          type: "error",
          message: "Failed to load customer data",
        });
      } finally {
        setLoading(false);
      }
    }
    loadCustomerData();
  }, [decodedEmail, licenseHolders]);

  if (loading) {
    return (
      <div className={styles.profilePage}>
        <div className={styles.loading}>Loading customer profile...</div>
      </div>
    );
  }

  if (licenses.length === 0) {
    return (
      <div className={styles.profilePage}>
        <div className={styles.notFound}>
          <h2>Customer not found</h2>
          <p>No license holder found for {decodedEmail}</p>
          <Button onClick={() => history.push("/admin")}>
            Back to Customers
          </Button>
        </div>
      </div>
    );
  }

  const primaryLicense = licenses[0];
  const fullName =
    `${primaryLicense.firstName || ""} ${
      primaryLicense.lastName || ""
    }`.trim() || decodedEmail;
  const tier = licenseTiers.find((t) => t.id === primaryLicense.licenseTierId);
  const tierName = tier ? getUserFacingSubscriptionName(tier.tier) : "Unknown";
  const createdDate = DateTime.fromISO(primaryLicense.created);
  const expirationDate = DateTime.fromISO(primaryLicense.expires);
  const aggregateStats =
    usage.length > 0 ? AdminUtility.getAggregateStats(usage) : null;

  const setupCriteria = {
    teamCreated: usage.some((u) => !!u.teamId),
    rosterInvited: aggregateStats
      ? aggregateStats.numberOfPlayers >= 10
      : false,
    playsAdded: aggregateStats ? aggregateStats.numberOfPlays >= 10 : false,
    quizzesAdded: aggregateStats
      ? aggregateStats.numberOfCustomQuizzes >= 10
      : false,
    lessonsAdded: aggregateStats
      ? aggregateStats.numberOfInstalls >= 10
      : false,
  };
  const setupCount = Object.values(setupCriteria).filter(Boolean).length;

  // Calculate health score (0-100)
  const calculateHealthScore = (): {
    score: number;
    label: string;
    color: string;
  } => {
    let score = 0;

    // Setup completeness (0-40 points)
    score += setupCount * 8;

    // Questions answered (0-30 points)
    const questions = aggregateStats?.numberOfQuestionsAnswered || 0;
    if (questions > 2000) score += 30;
    else if (questions > 500) score += 20;
    else if (questions > 100) score += 15;
    else if (questions > 10) score += 5;

    // Payment status (0-20 points)
    if (primaryLicense.paid) score += 20;
    else if (
      primaryLicense.licenseTierId &&
      !AdminUtility.createdOverXDaysAgo(primaryLicense.created, 30)
    )
      score += 10;

    // Account age / engagement ratio (0-10 points)
    const daysSinceCreation = Math.abs(createdDate.diffNow("days").days);
    if (daysSinceCreation > 0 && questions > 0) {
      const questionsPerDay = questions / daysSinceCreation;
      if (questionsPerDay > 5) score += 10;
      else if (questionsPerDay > 1) score += 7;
      else if (questionsPerDay > 0.1) score += 3;
    }

    score = Math.min(100, Math.max(0, score));

    if (score >= 70) return { score, label: "Healthy", color: "#4CAF50" };
    if (score >= 40) return { score, label: "At Risk", color: "#FFC107" };
    return { score, label: "Critical", color: "#F44336" };
  };

  const health = calculateHealthScore();

  return (
    <div className={styles.profilePage}>
      {/* Back Navigation */}
      <div className={styles.backNav}>
        <button
          className={styles.backButton}
          onClick={() => history.push("/admin")}
        >
          &larr; Back to Customers
        </button>
      </div>

      {/* Header Card */}
      <div className={styles.headerCard}>
        <div className={styles.headerCard__left}>
          <div className={styles.avatar}>
            {(primaryLicense.firstName || "?")[0]?.toUpperCase()}
          </div>
          <div className={styles.headerCard__info}>
            <h1 className={styles.customerName}>{fullName}</h1>
            <div className={styles.customerEmail}>{decodedEmail}</div>
            <div className={styles.customerMeta}>
              {primaryLicense.teamName && (
                <span className={styles.metaTag}>
                  {primaryLicense.teamName}
                </span>
              )}
              {primaryLicense.teamLevel && (
                <span className={styles.metaTag}>
                  {primaryLicense.teamLevel}
                </span>
              )}
              <span className={styles.metaTag}>{tierName}</span>
            </div>
          </div>
        </div>
        <div className={styles.headerCard__right}>
          <div
            className={styles.healthScore}
            style={{ borderColor: health.color }}
          >
            <div
              className={styles.healthScore__value}
              style={{ color: health.color }}
            >
              {health.score}
            </div>
            <div
              className={styles.healthScore__label}
              style={{ color: health.color }}
            >
              {health.label}
            </div>
          </div>
        </div>
      </div>

      {/* Tab Navigation */}
      <div className={styles.tabs}>
        <button
          className={`${styles.tab} ${
            activeTab === "overview" ? styles.tabActive : ""
          }`}
          onClick={() => setActiveTab("overview")}
        >
          Overview
        </button>
        <button
          className={`${styles.tab} ${
            activeTab === "license" ? styles.tabActive : ""
          }`}
          onClick={() => setActiveTab("license")}
        >
          License
        </button>
        <button
          className={`${styles.tab} ${
            activeTab === "billing" ? styles.tabActive : ""
          }`}
          onClick={() => setActiveTab("billing")}
        >
          Billing
        </button>
        <button
          className={`${styles.tab} ${
            activeTab === "hubspot" ? styles.tabActive : ""
          }`}
          onClick={() => setActiveTab("hubspot")}
        >
          HubSpot
        </button>
        <button
          className={`${styles.tab} ${
            activeTab === "usage" ? styles.tabActive : ""
          }`}
          onClick={() => setActiveTab("usage")}
        >
          Usage Analytics
        </button>
        <button
          className={`${styles.tab} ${
            activeTab === "intelligence" ? styles.tabActive : ""
          }`}
          onClick={() => setActiveTab("intelligence")}
        >
          Customer Intelligence
        </button>
        <button
          className={`${styles.tab} ${
            activeTab === "communications" ? styles.tabActive : ""
          }`}
          onClick={() => setActiveTab("communications")}
        >
          Communications
        </button>
      </div>

      {/* Tab Content */}
      {activeTab === "overview" && (
        <div className={styles.tabContent}>
          <div className={styles.cardGrid}>
            {/* Account Summary Card */}
            <div className={styles.card}>
              <h3 className={styles.card__title}>Account Summary</h3>
              <div className={styles.statGrid}>
                <div className={styles.stat}>
                  <div className={styles.stat__label}>Tier</div>
                  <div className={styles.stat__value}>{tierName}</div>
                </div>
                <div className={styles.stat}>
                  <div className={styles.stat__label}>Created</div>
                  <div className={styles.stat__value}>
                    {createdDate.toFormat("MMM d, yyyy")}
                  </div>
                </div>
                <div className={styles.stat}>
                  <div className={styles.stat__label}>Expires</div>
                  <div className={styles.stat__value}>
                    {expirationDate.toFormat("MMM d, yyyy")}
                  </div>
                </div>
                <div className={styles.stat}>
                  <div className={styles.stat__label}>Payment</div>
                  <div className={styles.stat__value}>
                    {primaryLicense.paid ? "Paid" : "Unpaid"}
                  </div>
                </div>
                <div className={styles.stat}>
                  <div className={styles.stat__label}>Invoice</div>
                  <div className={styles.stat__value}>
                    {primaryLicense.invoiceNumber || "None"}
                  </div>
                </div>
                <div className={styles.stat}>
                  <div className={styles.stat__label}>Licenses</div>
                  <div className={styles.stat__value}>{licenses.length}</div>
                </div>
              </div>
            </div>

            {/* Usage Stats Card */}
            <div className={styles.card}>
              <h3 className={styles.card__title}>Usage Stats</h3>
              {aggregateStats ? (
                <div className={styles.statGrid}>
                  <div className={styles.stat}>
                    <div className={styles.stat__label}>Coaches</div>
                    <div className={styles.stat__value}>
                      {aggregateStats.numberOfCoaches}
                    </div>
                  </div>
                  <div className={styles.stat}>
                    <div className={styles.stat__label}>Players</div>
                    <div className={styles.stat__value}>
                      {aggregateStats.numberOfPlayers}
                    </div>
                  </div>
                  <div className={styles.stat}>
                    <div className={styles.stat__label}>Plays</div>
                    <div className={styles.stat__value}>
                      {aggregateStats.numberOfPlays}
                    </div>
                  </div>
                  <div className={styles.stat}>
                    <div className={styles.stat__label}>Flashcards</div>
                    <div className={styles.stat__value}>
                      {aggregateStats.numberOfCustomQuizzes}
                    </div>
                  </div>
                  <div className={styles.stat}>
                    <div className={styles.stat__label}>Lessons</div>
                    <div className={styles.stat__value}>
                      {aggregateStats.numberOfInstalls}
                    </div>
                  </div>
                  <div className={styles.stat}>
                    <div className={styles.stat__label}>Questions Answered</div>
                    <div className={styles.stat__value}>
                      {aggregateStats.numberOfQuestionsAnswered.toLocaleString()}
                    </div>
                  </div>
                </div>
              ) : (
                <div className={styles.emptyState}>No usage data available</div>
              )}
            </div>

            {/* Recent Activity Card — shows the last time a coach edited
                content and the last time a player answered a question.
                "—" while engagement endpoint is loading or unavailable. */}
            <div className={styles.card}>
              <h3 className={styles.card__title}>Recent Activity</h3>
              <div className={styles.statGrid}>
                <div className={styles.stat}>
                  <div className={styles.stat__label}>Last Coach Activity</div>
                  <div className={styles.stat__value}>
                    {formatRelativeActivity(engagement?.lastCoachActivityAt)}
                  </div>
                </div>
                <div className={styles.stat}>
                  <div className={styles.stat__label}>Last Player Activity</div>
                  <div className={styles.stat__value}>
                    {formatRelativeActivity(engagement?.lastPlayerActivityAt)}
                  </div>
                </div>
                <div className={styles.stat}>
                  <div className={styles.stat__label}>Questions, Past 7d</div>
                  <div className={styles.stat__value}>
                    {typeof engagement?.questions7d === "number"
                      ? engagement.questions7d.toLocaleString()
                      : "—"}
                  </div>
                </div>
                <div className={styles.stat}>
                  <div className={styles.stat__label}>Questions, Past 90d</div>
                  <div className={styles.stat__value}>
                    {typeof engagement?.questions90d === "number"
                      ? engagement.questions90d.toLocaleString()
                      : "—"}
                  </div>
                </div>
              </div>
            </div>

            {/* Team Setup Progress Card */}
            <div className={styles.card}>
              <h3 className={styles.card__title}>
                Team Setup ({setupCount}/5)
              </h3>
              <div className={styles.setupList}>
                {Object.entries({
                  "Team Created": setupCriteria.teamCreated,
                  "10+ Players Invited": setupCriteria.rosterInvited,
                  "10+ Plays Added": setupCriteria.playsAdded,
                  "10+ Flashcards Added": setupCriteria.quizzesAdded,
                  "10+ Lessons Added": setupCriteria.lessonsAdded,
                }).map(([label, completed]) => (
                  <div key={label} className={styles.setupItem}>
                    {completed ? (
                      <TeamSetupIconCheck />
                    ) : (
                      <TeamSetupIconEmpty />
                    )}
                    <span
                      className={
                        completed
                          ? styles.setupItem__done
                          : styles.setupItem__pending
                      }
                    >
                      {label}
                    </span>
                  </div>
                ))}
              </div>
            </div>

            {/* Notes Card */}
            <div className={styles.card}>
              <h3 className={styles.card__title}>Notes</h3>
              <div className={styles.notesSection}>
                {notes.length === 0 && (
                  <div className={styles.emptyState}>
                    No notes yet. Add a note about this customer.
                  </div>
                )}
                {notes.map((note) => (
                  <div key={note.id} className={styles.noteItem}>
                    <div className={styles.noteItem__text}>{note.text}</div>
                    <div className={styles.noteItem__meta}>
                      {note.author} &middot; {note.createdAt}
                    </div>
                  </div>
                ))}
                <div className={styles.noteInput}>
                  <textarea
                    className={styles.noteTextarea}
                    placeholder="Add a note about this customer..."
                    value={newNote}
                    onChange={(e) => setNewNote(e.target.value)}
                    rows={3}
                  />
                  <Button
                    onClick={() => {
                      if (newNote.trim()) {
                        setNotes([
                          ...notes,
                          {
                            id: Date.now().toString(),
                            text: newNote.trim(),
                            createdAt: new Date().toLocaleDateString(),
                            author: "Admin",
                          },
                        ]);
                        setNewNote("");
                      }
                    }}
                  >
                    Add Note
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {activeTab === "license" && (
        <LicenseTab licenses={licenses} usage={usage} tnReps={tnReps} />
      )}

      {activeTab === "billing" && <BillingTab email={decodedEmail} />}

      {activeTab === "hubspot" && <HubspotTab email={decodedEmail} />}

      {activeTab === "usage" && <UsageAnalyticsTab email={decodedEmail} />}

      {activeTab === "intelligence" && (
        <CustomerIntelligenceTab
          email={decodedEmail}
          owner={fullName}
          teamName={licenses.length > 0 ? licenses[0].teamName || "-" : "-"}
          dispatchToast={dispatchToast}
        />
      )}

      {activeTab === "communications" && (
        <CommunicationsTab email={decodedEmail} dispatchToast={dispatchToast} />
      )}
    </div>
  );
};

// ============================================================
// Usage Analytics Sub-Tab
// ============================================================
const UsageAnalyticsTab: React.FC<{ email: string }> = ({ email }) => {
  // Daily-resolution event timeseries (questions per day +
  // coaches/players added per day) — new endpoint, raw events back to
  // 2021. Replaces the snapshot-derived sparse history.
  const [timeseries, setTimeseries] = useState<{
    questionsPerDay: { date: string; count: number }[];
    usersAddedPerDay: { date: string; count: number }[];
  } | null>(null);
  const [mockHolder, setMockHolder] = useState<any>(null);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      try {
        const ts = await APIService.ADMIN_ANALYTICS.GET_CUSTOMER_TIMESERIES(
          email
        );
        if (!cancelled) setTimeseries(ts);
      } catch (e) {
        console.error("Failed to load customer timeseries", e);
        if (!cancelled) setTimeseries(null);
      }
      try {
        await AdminDataService.initialize();
        if (!cancelled) setMockHolder(AdminDataService.getLicenseHolder(email));
      } catch {
        // mock fallback optional
      }
    }
    load();
    return () => {
      cancelled = true;
    };
  }, [email]);

  // Cumulative series via running sum over the daily counts. Same data
  // powers both the cumulative line and the daily bars/line.
  function runningSum(rows: { date: string; count: number }[]) {
    let total = 0;
    return rows.map((r) => {
      total += r.count;
      return { x: r.date, y: total };
    });
  }
  function asXY(rows: { date: string; count: number }[]) {
    return rows.map((r) => ({ x: r.date, y: r.count }));
  }

  // Pre-compute the full series once. Each chart card filters by its
  // own range below; running-sum runs on the FULL data so the
  // cumulative line shows the running total at each visible day.
  const fullQuestionsCum = timeseries
    ? runningSum(timeseries.questionsPerDay)
    : [];
  const fullUsersCum = timeseries
    ? runningSum(timeseries.usersAddedPerDay)
    : [];
  const fullPerDay = timeseries ? asXY(timeseries.questionsPerDay) : [];

  const todayUTC = new Date().toISOString().slice(0, 10);
  const earliestQuestionsCum = fullQuestionsCum[0]?.x ?? todayUTC;
  const earliestUsersCum = fullUsersCum[0]?.x ?? todayUTC;
  const earliestPerDay = fullPerDay[0]?.x ?? todayUTC;

  const questionsCumRange = useChartRange(earliestQuestionsCum);
  const perDayRange = useChartRange(earliestPerDay);
  const usersCumRange = useChartRange(earliestUsersCum);

  const questionsSeries = [
    {
      id: "Questions",
      data: filterByRange(fullQuestionsCum, questionsCumRange.range),
    },
  ];
  const usersSeries = [
    {
      id: "Users",
      data: filterByRange(fullUsersCum, usersCumRange.range),
    },
  ];
  const perDaySeries = [
    {
      id: "Per Day",
      color: "#35d3ed",
      data: filterByRange(fullPerDay, perDayRange.range),
    },
  ];
  const hasHistory = !!timeseries && timeseries.questionsPerDay.length > 0;
  const hasPerDayData = !!timeseries && timeseries.questionsPerDay.length > 0;

  const nivoTheme = {
    textColor: "#ababb7",
    fontSize: 11,
    grid: { line: { stroke: "#424258", strokeWidth: 1 } },
    axis: {
      ticks: { text: { fill: "#ababb7", fontSize: 10 } },
      legend: { text: { fill: "#ababb7", fontSize: 12 } },
    },
    crosshair: { line: { stroke: "#ffbc17", strokeWidth: 1 } },
    tooltip: {
      container: {
        background: "#282B3A",
        color: "#ffffff",
        fontSize: 12,
        borderRadius: 6,
        border: "1px solid #424258",
      },
    },
  };

  const lineXScale = {
    type: "time" as const,
    format: "%Y-%m-%d",
    precision: "day" as const,
    useUTC: true,
  };
  const lineAxisBottom = {
    format: "%b %Y",
    tickValues: 6,
    tickRotation: -45,
    legendOffset: 40,
  };

  return (
    <div className={styles.tabContent}>
      {hasHistory ? (
        <>
          <div className={styles.card} style={{ marginBottom: 16 }}>
            <h3 className={styles.card__title}>
              Total Questions Answered Over Time
              {timeseries
                ? ` (${timeseries.questionsPerDay.length.toLocaleString()} active days)`
                : ""}
            </h3>
            <ChartRangePicker
              earliest={earliestQuestionsCum}
              selected={questionsCumRange.range}
              onChange={questionsCumRange.setRange}
            />
            <div style={{ height: 280 }}>
              <ResponsiveLine
                data={questionsSeries}
                theme={nivoTheme}
                margin={{ top: 20, right: 30, bottom: 50, left: 70 }}
                xScale={{
                  ...lineXScale,
                  min: questionsCumRange.range.start,
                  max: questionsCumRange.range.end,
                }}
                xFormat="time:%Y-%m-%d"
                yScale={{ type: "linear", min: 0, max: "auto" }}
                axisBottom={lineAxisBottom}
                axisLeft={{
                  format: (v) =>
                    typeof v === "number" ? v.toLocaleString() : v,
                }}
                colors={["#00b2ff"]}
                lineWidth={3}
                pointSize={0}
                pointBorderWidth={2}
                pointBorderColor="#282B3A"
                enableArea
                areaBaselineValue={0}
                areaOpacity={0.1}
                useMesh
                enableGridX={false}
              />
            </div>
          </div>

          <div className={styles.card} style={{ marginBottom: 16 }}>
            <h3 className={styles.card__title}>Questions Answered Per Day</h3>
            {!hasPerDayData ? (
              <p style={{ fontSize: 12, color: "#ababb7", marginTop: 4 }}>
                No question activity recorded for this customer.
              </p>
            ) : (
              <>
                <ChartRangePicker
                  earliest={earliestPerDay}
                  selected={perDayRange.range}
                  onChange={perDayRange.setRange}
                />
                <p
                  style={{
                    fontSize: 12,
                    color: "#ababb7",
                    marginTop: -4,
                    marginBottom: 8,
                  }}
                >
                  Real daily counts from the question_statistic event log.
                </p>
                <div style={{ height: 240 }}>
                  <ResponsiveLine
                    data={perDaySeries}
                    theme={nivoTheme}
                    margin={{ top: 20, right: 30, bottom: 50, left: 70 }}
                    xScale={{
                      ...lineXScale,
                      min: perDayRange.range.start,
                      max: perDayRange.range.end,
                    }}
                    xFormat="time:%Y-%m-%d"
                    yScale={{ type: "linear", min: 0, max: "auto" }}
                    axisBottom={lineAxisBottom}
                    axisLeft={{
                      format: (v) =>
                        typeof v === "number" ? v.toLocaleString() : v,
                    }}
                    colors={["#35d3ed"]}
                    lineWidth={1.5}
                    pointSize={0}
                    enableArea
                    areaBaselineValue={0}
                    areaOpacity={0.15}
                    useMesh
                    enableGridX={false}
                  />
                </div>
              </>
            )}
          </div>

          <div className={styles.card} style={{ marginBottom: 16 }}>
            <h3 className={styles.card__title}>
              Users (Coaches + Players) Over Time
            </h3>
            <ChartRangePicker
              earliest={earliestUsersCum}
              selected={usersCumRange.range}
              onChange={usersCumRange.setRange}
            />
            <div style={{ height: 240 }}>
              <ResponsiveLine
                data={usersSeries}
                theme={nivoTheme}
                margin={{ top: 20, right: 30, bottom: 50, left: 60 }}
                xScale={{
                  ...lineXScale,
                  min: usersCumRange.range.start,
                  max: usersCumRange.range.end,
                }}
                xFormat="time:%Y-%m-%d"
                yScale={{ type: "linear", min: 0, max: "auto" }}
                axisBottom={lineAxisBottom}
                axisLeft={{
                  format: (v) =>
                    typeof v === "number" ? v.toLocaleString() : v,
                }}
                colors={["#ffbc17"]}
                lineWidth={3}
                pointSize={0}
                pointBorderWidth={2}
                pointBorderColor="#282B3A"
                enableArea
                areaBaselineValue={0}
                areaOpacity={0.15}
                useMesh
                enableGridX={false}
              />
            </div>
          </div>
          {mockHolder && (
            <div className={styles.card}>
              <h3 className={styles.card__title}>Account Summary</h3>
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "1fr 1fr 1fr",
                  gap: 16,
                }}
              >
                <div>
                  <div style={{ fontSize: 11, color: "#ababb7" }}>
                    Health Score
                  </div>
                  <div
                    style={{
                      fontSize: 24,
                      fontWeight: 700,
                      color:
                        mockHolder.healthScore >= 70
                          ? "#4CAF50"
                          : mockHolder.healthScore >= 40
                          ? "#FFC107"
                          : "#F44336",
                    }}
                  >
                    {mockHolder.healthScore} — {mockHolder.healthLabel}
                  </div>
                </div>
                <div>
                  <div style={{ fontSize: 11, color: "#ababb7" }}>
                    Total Questions
                  </div>
                  <div style={{ fontSize: 24, fontWeight: 700 }}>
                    {mockHolder.questions.toLocaleString()}
                  </div>
                </div>
                <div>
                  <div style={{ fontSize: 11, color: "#ababb7" }}>Users</div>
                  <div style={{ fontSize: 24, fontWeight: 700 }}>
                    {mockHolder.users.toLocaleString()}
                  </div>
                </div>
              </div>
            </div>
          )}
        </>
      ) : (
        <div className={styles.card}>
          <h3 className={styles.card__title}>Usage Analytics</h3>
          <div className={styles.emptyState}>
            <p>
              {timeseries
                ? "No question events recorded for this customer yet."
                : "Loading historical event data..."}
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

// ============================================================
// License Sub-Tab — embeds ManageLicenseModal2 inline so admins can edit
// tier/payment/expiration/etc. without leaving the customer profile.
// Builds the LicenseOwnerListRow shape the modal expects from the
// already-loaded license + usage data.
// ============================================================
const LicenseTab: React.FC<{
  licenses: LicenseHolderModel[];
  usage: LicenseUsageModel[];
  tnReps: UserProfileModel[];
}> = ({ licenses, usage, tnReps }) => {
  const { licenseTiers } = useContext(LicenseTiersContext);
  const [refreshKey, setRefreshKey] = useState(0);

  const licenseOne = licenses[0];
  const licenseTier = licenseTiers.find(
    (t) => t.id === licenseOne?.licenseTierId
  );

  if (!licenseOne || !licenseTier) {
    return (
      <div className={styles.tabContent}>
        <div className={styles.card}>
          <h3 className={styles.card__title}>License</h3>
          <div className={styles.emptyState}>
            <p>No license data available for this customer.</p>
          </div>
        </div>
      </div>
    );
  }

  const created = AdminUtility.getEarliestCreationDate(licenses);
  const setupCriteria = new Set<SetupCriteria>();
  let usersCount = 0;
  let questionsCount = 0;
  for (const u of usage) {
    usersCount += u.numberOfPlayers + u.numberOfCoaches;
    questionsCount +=
      (u.numberOfQuestionsAnswered || 0) +
      (u.numberOfQuizQuestionsAnswered || 0) +
      (u.numberOfInstallQuestionsAnswered || 0);
    if (u.teamId) setupCriteria.add(SetupCriteria.teamCreated);
    if (u.numberOfPlayers >= 10) setupCriteria.add(SetupCriteria.rosterInvited);
    if (u.numberOfPlays >= 10) setupCriteria.add(SetupCriteria.playsAdded);
    if (u.numberOfCustomQuizzes >= 10)
      setupCriteria.add(SetupCriteria.quizzesAdded);
    if (u.numberOfInstalls >= 10) setupCriteria.add(SetupCriteria.lessonsAdded);
  }

  const row: LicenseOwnerListRow = {
    id: licenseOne.id,
    owner:
      `${licenseOne.firstName || ""} ${licenseOne.lastName || ""}`.trim() ||
      licenseOne.email,
    email: licenseOne.email,
    tier: licenseTier,
    payment: AdminUtility.getPaymentCategory(
      licenseOne,
      created.toLocaleString(),
      licenseOne.invoiceNumber
    ),
    hubspotPaymentStatus: AdminUtility.getPaymentCategory(
      licenseOne,
      created.toLocaleString(),
      licenseOne.invoiceNumber,
      "HUBSPOT"
    ),
    tnRep: licenseOne.tnRepresentativeUserId,
    dateCreated: created,
    users: usersCount,
    timeSpent: 0,
    questions: questionsCount,
    teamSetup: setupCriteria.size,
    firstTeamName: licenseOne.teamName || "-",
    firstTeamLevel: licenseOne.teamLevel || "-",
    licenseHolderData: licenses,
    licenseStats: usage,
    setupCriteria,
    expirationDate: licenseOne.expires
      ? DateTime.fromISO(licenseOne.expires)
      : DateTime.fromISO(new Date().toISOString()),
  };

  return (
    <div className={styles.tabContent}>
      <ManageLicenseModal2
        key={refreshKey}
        licenseData={row}
        updates={refreshKey}
        tnReps={tnReps}
        closeCallback={() => setRefreshKey((k) => k + 1)}
        embedded
      />
    </div>
  );
};

// ============================================================
// Customer Intelligence Sub-Tab
// ============================================================
const CustomerIntelligenceTab: React.FC<{
  email: string;
  owner: string;
  teamName: string;
  dispatchToast: any;
}> = ({ email, owner, teamName, dispatchToast }) => {
  const [mockHolder, setMockHolder] = useState<any>(null);

  useEffect(() => {
    async function load() {
      await AdminDataService.initialize();
      setMockHolder(AdminDataService.getLicenseHolder(email));
    }
    load();
  }, [email]);

  // Extract school domain from email
  const domain = email.split("@")[1] || "";
  const isSchoolEmail =
    domain.includes(".edu") ||
    domain.includes(".org") ||
    domain.includes("k12") ||
    domain.includes("isd");

  return (
    <div className={styles.tabContent}>
      <div className={styles.cardGrid}>
        <div className={styles.card}>
          <h3 className={styles.card__title}>Account Details</h3>
          <div className={styles.statGrid}>
            <div>
              <div style={{ fontSize: 11, color: "#ababb7" }}>Owner</div>
              <div style={{ fontSize: 14, fontWeight: 700 }}>{owner}</div>
            </div>
            <div>
              <div style={{ fontSize: 11, color: "#ababb7" }}>Email</div>
              <div style={{ fontSize: 14, fontWeight: 700 }}>{email}</div>
            </div>
            <div>
              <div style={{ fontSize: 11, color: "#ababb7" }}>Domain</div>
              <div style={{ fontSize: 14, fontWeight: 700 }}>{domain}</div>
            </div>
            <div>
              <div style={{ fontSize: 11, color: "#ababb7" }}>Team Name</div>
              <div style={{ fontSize: 14, fontWeight: 700 }}>
                {mockHolder?.teamName || teamName}
              </div>
            </div>
            <div>
              <div style={{ fontSize: 11, color: "#ababb7" }}>Team Level</div>
              <div style={{ fontSize: 14, fontWeight: 700 }}>
                {mockHolder?.teamLevel || "-"}
              </div>
            </div>
            <div>
              <div style={{ fontSize: 11, color: "#ababb7" }}>Tier</div>
              <div style={{ fontSize: 14, fontWeight: 700 }}>
                {mockHolder?.tier || "-"}
              </div>
            </div>
          </div>
        </div>

        <div className={styles.card}>
          <h3 className={styles.card__title}>Research Status</h3>
          <div className={styles.emptyState}>
            <p>
              {isSchoolEmail
                ? `School email detected (${domain}). Auto-research can look up school details, MaxPreps records, and social profiles.`
                : `Personal email (${domain}). Limited auto-research available. School email would provide better results.`}
            </p>
            <p style={{ marginTop: 12 }}>Planned research capabilities:</p>
            <ul className={styles.featureList}>
              <li>School/organization details &amp; enrollment</li>
              <li>Coach role and title</li>
              <li>Social media profiles (Twitter/X, LinkedIn)</li>
              <li>MaxPreps win/loss records &amp; rankings</li>
              <li>Conference and division info</li>
              <li>News mentions</li>
            </ul>
            <div style={{ marginTop: 16 }}>
              <Button
                onClick={() =>
                  dispatchToast({
                    type: "info",
                    message:
                      "Auto-research integration coming soon. Will pull data from MaxPreps, school directories, and social media.",
                  })
                }
              >
                Research This Customer
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// ============================================================
// Communications Sub-Tab
// ============================================================
const CommunicationsTab: React.FC<{
  email: string;
  dispatchToast: any;
}> = ({ email, dispatchToast }) => {
  const [composeChannel, setComposeChannel] = useState<"email" | "sms" | null>(
    null
  );
  const [composeBody, setComposeBody] = useState("");
  const [composeSubject, setComposeSubject] = useState("");
  const [composePhone, setComposePhone] = useState("");
  const [sending, setSending] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);

  const showCompose = composeChannel !== null;
  const onCancel = () => {
    setComposeChannel(null);
    setComposeBody("");
    setComposeSubject("");
  };

  const onSend = async () => {
    if (sending || !composeChannel) return;
    if (!composeBody) {
      dispatchToast({ type: "error", message: "Body is required" });
      return;
    }
    if (composeChannel === "email" && !composeSubject) {
      dispatchToast({ type: "error", message: "Subject is required" });
      return;
    }
    if (composeChannel === "sms" && !composePhone) {
      dispatchToast({
        type: "error",
        message: "Phone number is required (E.164, e.g. +18014287824)",
      });
      return;
    }
    setSending(true);
    try {
      const result = await APIService.COMMUNICATIONS.SEND({
        channel: composeChannel,
        recipient: composeChannel === "email" ? email : composePhone,
        subject: composeChannel === "email" ? composeSubject : undefined,
        body: composeBody,
      });
      onCancel();
      const blockedNote =
        result.status === "blocked_dev"
          ? " (blocked in dev — recipient not in allowlist)"
          : "";
      dispatchToast({
        type: result.status === "failed" ? "error" : "success",
        message: `${composeChannel === "email" ? "Email" : "SMS"} ${
          result.status
        }${blockedNote}`,
      });
      setReloadKey((k) => k + 1);
    } catch (e: any) {
      dispatchToast({
        type: "error",
        message:
          e?.response?.data?.error ||
          `Send failed: ${e?.message || "unknown error"}`,
      });
    } finally {
      setSending(false);
    }
  };

  return (
    <div className={styles.tabContent}>
      {/* Compose Buttons */}
      <div className={styles.commActions} style={{ marginBottom: 16 }}>
        <Button onClick={() => setComposeChannel("email")}>
          Compose Email
        </Button>
        <Button onClick={() => setComposeChannel("sms")}>Compose SMS</Button>
      </div>

      {/* Compose Section */}
      {showCompose && (
        <div className={styles.card} style={{ marginBottom: 16 }}>
          <h3 className={styles.card__title}>
            New {composeChannel === "email" ? "Email" : "SMS"}
          </h3>
          <div style={{ marginBottom: 12 }}>
            <div
              style={{
                fontSize: 12,
                color: "#ababb7",
                marginBottom: 4,
              }}
            >
              To
            </div>
            {composeChannel === "email" ? (
              <input
                type="text"
                value={email}
                readOnly
                style={{
                  width: "100%",
                  background: "#353849",
                  border: "1px solid #424258",
                  borderRadius: 6,
                  padding: "8px 12px",
                  color: "#fff",
                  fontSize: 13,
                }}
              />
            ) : (
              <input
                type="tel"
                value={composePhone}
                onChange={(e) => setComposePhone(e.target.value)}
                placeholder="+18014287824 (E.164)"
                style={{
                  width: "100%",
                  background: "#353849",
                  border: "1px solid #424258",
                  borderRadius: 6,
                  padding: "8px 12px",
                  color: "#fff",
                  fontSize: 13,
                }}
              />
            )}
          </div>
          {composeChannel === "email" && (
            <div style={{ marginBottom: 12 }}>
              <div
                style={{
                  fontSize: 12,
                  color: "#ababb7",
                  marginBottom: 4,
                }}
              >
                Subject
              </div>
              <input
                type="text"
                value={composeSubject}
                onChange={(e) => setComposeSubject(e.target.value)}
                placeholder="Enter subject..."
                style={{
                  width: "100%",
                  background: "#353849",
                  border: "1px solid #424258",
                  borderRadius: 6,
                  padding: "8px 12px",
                  color: "#fff",
                  fontSize: 13,
                }}
              />
            </div>
          )}
          <div style={{ marginBottom: 12 }}>
            <div
              style={{
                fontSize: 12,
                color: "#ababb7",
                marginBottom: 4,
              }}
            >
              {composeChannel === "email"
                ? "Message (HTML allowed)"
                : "Message"}
            </div>
            <textarea
              value={composeBody}
              onChange={(e) => setComposeBody(e.target.value)}
              placeholder={
                composeChannel === "sms"
                  ? "Keep under 160 chars to stay on a single SMS segment"
                  : "Write your message... (HTML allowed)"
              }
              rows={6}
              style={{
                width: "100%",
                background: "#353849",
                border: "1px solid #424258",
                borderRadius: 6,
                padding: "8px 12px",
                color: "#fff",
                fontSize: 13,
                fontFamily: "Quantico, sans-serif",
                resize: "vertical",
              }}
            />
            {composeChannel === "sms" && (
              <div
                style={{
                  fontSize: 11,
                  color: composeBody.length > 160 ? "#FFC107" : "#ababb7",
                  marginTop: 4,
                  textAlign: "right",
                }}
              >
                {composeBody.length} chars
                {composeBody.length > 160 ? " (will split across SMS)" : ""}
              </div>
            )}
          </div>
          <div
            style={{
              display: "flex",
              gap: 10,
              justifyContent: "flex-end",
            }}
          >
            <Button onClick={onCancel}>Cancel</Button>
            <Button onClick={onSend} disabled={sending}>
              {sending ? "Sending..." : "Send"}
            </Button>
          </div>
        </div>
      )}

      {/* Communication History (real send log filtered by recipient) */}
      <div className={styles.card}>
        <h3 className={styles.card__title}>Communication History</h3>
        <SendLogTable
          key={reloadKey}
          recipientFilter={email}
          hideRecipientFilterInput
        />
      </div>
    </div>
  );
};

export default CustomerProfile;
